/**
 * Account Page
 *
 * Main account dashboard for authenticated users.
 * Reads user info from Supabase Auth (user) and the customers table (customer).
 */

import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import LogoutIcon from '@mui/icons-material/Logout';

const Account = () => {
  const { user, customer, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
  };

  const displayName = customer?.first_name || user?.email || 'Valued Customer';

  return (
    <Container maxWidth="md" sx={{ my: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#8B4513' }}>
        My Account
      </Typography>

      <Grid container spacing={3}>
        {/* Welcome Section */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              Welcome, {displayName}!
            </Typography>
            <Typography variant="body1">
              Manage your account information, addresses, and view your order history.
            </Typography>
          </Paper>
        </Grid>

        {/* Account Navigation */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#8B4513' }}>
              Account Navigation
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List component="nav" aria-label="account navigation">
              <ListItem button component={Link} to="/account/profile">
                <ListItemIcon>
                  <PersonIcon sx={{ color: '#8B4513' }} />
                </ListItemIcon>
                <ListItemText primary="Profile" />
              </ListItem>
              <ListItem button component={Link} to="/account/addresses">
                <ListItemIcon>
                  <LocationOnIcon sx={{ color: '#8B4513' }} />
                </ListItemIcon>
                <ListItemText primary="Addresses" />
              </ListItem>
              <ListItem button component={Link} to="/account/orders">
                <ListItemIcon>
                  <ShoppingBagIcon sx={{ color: '#8B4513' }} />
                </ListItemIcon>
                <ListItemText primary="Orders" />
              </ListItem>
              <ListItem button onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon sx={{ color: '#8B4513' }} />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Account Summary */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#8B4513' }}>
              Account Summary
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Contact Information
              </Typography>
              <Typography variant="body1">
                {customer?.first_name} {customer?.last_name}
              </Typography>
              <Typography variant="body1">
                {user?.email}
              </Typography>
              <Typography variant="body1">
                {customer?.phone || 'No phone number provided'}
              </Typography>
              <Button
                component={Link}
                to="/account/profile"
                variant="outlined"
                size="small"
                sx={{
                  mt: 1,
                  color: '#8B4513',
                  borderColor: '#8B4513',
                  '&:hover': { borderColor: '#6d3610' }
                }}
              >
                Edit Profile
              </Button>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Default Shipping Address
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Visit the{' '}
                <Link to="/account/addresses" style={{ color: '#8B4513' }}>Addresses</Link>
                {' '}page to manage your saved addresses.
              </Typography>
              <Button
                component={Link}
                to="/account/addresses"
                variant="outlined"
                size="small"
                sx={{
                  mt: 1,
                  color: '#8B4513',
                  borderColor: '#8B4513',
                  '&:hover': { borderColor: '#6d3610' }
                }}
              >
                Manage Addresses
              </Button>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Recent Orders
              </Typography>
              <Button
                component={Link}
                to="/account/orders"
                variant="contained"
                sx={{ mt: 1, bgcolor: '#8B4513', '&:hover': { bgcolor: '#6d3610' } }}
              >
                View All Orders
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Account;
